import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { down } from "../components/images/svg/icons";

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  triggerClassName?: string;
  menuClassName?: string;
  optionClassName?: string;
  fullWidth?: boolean;
  parentRelativeWidth?: boolean; // New prop for parent-relative positioning
  showIcon?: boolean;
  icon?: ReactNode;
  renderOption?: (option: DropdownOption, isSelected: boolean) => ReactNode;
  renderTrigger?: (selectedOption: DropdownOption | null, isOpen: boolean) => ReactNode;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onSelect,
  placeholder = 'Select option',
  disabled = false,
  className = '',
  style = {},
  triggerClassName = '',
  menuClassName = '',
  optionClassName = '',
  fullWidth = true,
  parentRelativeWidth = false,
  showIcon = true,
  icon,
  renderOption,
  renderTrigger,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    if (!disabled) {
      onSelect(optionValue);
      setIsOpen(false);
    }
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const selectedOption = options.find(option => option.value === value) || null;

  const defaultTriggerContent = (
    <>
      <span>{selectedOption ? selectedOption.label : placeholder}</span>
      {showIcon && (
        <span className="mr-2 mb-1">
          {icon || <img src={down} alt="" />}
        </span>
      )}
    </>
  );

  const defaultOptionContent = (option: DropdownOption, isSelected: boolean) => (
    <span>{option.label}</span>
  );

  return (
    <div 
      className={`position-relative  ${className}`} 
      style={style} 
      ref={dropdownRef}
    >
      {/* Dropdown Trigger */}
      <button
        type="button"
        className={`btn btn-link fw-semibold d-flex align-items-center justify-content-between p-0 text-decoration-none w-100 mx-1${triggerClassName} ${disabled ? 'disabled' : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        style={{
          color: '#68727D',
          fontSize: '14px',
          fontWeight: 400,
          border: 'none',
          background: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {renderTrigger ? renderTrigger(selectedOption, isOpen) : defaultTriggerContent}
      </button>
      {isOpen && !disabled && (
        <div
          className={`position-absolute bg-white border rounded-3 shadow-lg p-2 ${menuClassName}`}
          style={{
            top: '100%',
            left: parentRelativeWidth ? 0 : (fullWidth ? '0' : 'auto'),
            right: parentRelativeWidth ? 0 : (fullWidth ? '0' : 'auto'),
            width: parentRelativeWidth ? '100%' : (fullWidth ? '100%' : 'auto'),
            transform: parentRelativeWidth ? 'translateX(calc(-100% + 80px))' : 'none',
            zIndex: 1000,
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            backgroundColor: '#ffffff',
            marginTop: '4px',
            minWidth: fullWidth && !parentRelativeWidth ? 'auto' : '200px',
            maxHeight: '300px',
            overflowY: 'auto',
          }}
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            const isOptionDisabled = option.disabled || false;
            
            return (
              <button
                key={option.value}
                type="button"
                className={`btn w-100 rounded-2 mb-1 border-0 px-3 py-2 ${isSelected ? 'active' : ''} ${optionClassName} ${isOptionDisabled ? 'disabled' : ''}`}
                onClick={() => handleSelect(option.value)}
                disabled={isOptionDisabled}
                style={{
                  backgroundColor: isSelected ? '#F3F4F6' : 'transparent',
                  color: isOptionDisabled ? '#9CA3AF' : '#374151',
                  fontSize: '14px',
                  fontWeight: 400,
                  border: 'none',
                  transition: 'background-color 0.2s ease',
                  textAlign: 'start',
                  cursor: isOptionDisabled ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected && !isOptionDisabled) {
                    e.currentTarget.style.backgroundColor = '#F9FAFB';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected && !isOptionDisabled) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {renderOption ? renderOption(option, isSelected) : defaultOptionContent(option, isSelected)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
